import React, { useState, useEffect, useCallback } from "react";
import {
  FaMapPin,
  FaCity,
  FaGlobe,
  FaHome,
  FaMailBulk,
  FaSearchLocation,
} from "react-icons/fa";
import countries from "../location/countries";

const LocationInformation = ({ formData, onLocationDataChange }) => {
  const [geoError, setGeoError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [latitude, setLatitude] = useState(formData.latitude || null);
  const [longitude, setLongitude] = useState(formData.longitude || null);
  const [formErrors, setFormErrors] = useState({});

  /* ==============================
     🌍 Detect User Timezone (Auto)
  ============================== */
  useEffect(() => {
    const detectedTimeZone =
      Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (!formData.timeZone) {
      onLocationDataChange("timeZone", detectedTimeZone);
    }
  }, [formData.timeZone, onLocationDataChange]);

  /* ==============================
     📍 Fetch Geolocation
  ============================== */
  const getGeoLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by your browser.");
      return;
    }

    setIsFetching(true);
    setGeoError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        // Store locally
        setLatitude(latitude);
        setLongitude(longitude);

        // Send to parent
        onLocationDataChange("latitude", latitude);
        onLocationDataChange("longitude", longitude);

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );

          const data = await response.json();

          if (data?.address) {
            const { address } = data;

            onLocationDataChange(
              "city",
              address.city || address.town || address.village || ""
            );
            onLocationDataChange("district", address.county || "");
            onLocationDataChange("state", address.state || "");
            onLocationDataChange("country", address.country || "");
            onLocationDataChange("postalCode", address.postcode || "");
          } else {
            setGeoError("Unable to retrieve location details.");
          }
        } catch (error) {
          setGeoError("Error fetching location data. Please try again.");
        } finally {
          setIsFetching(false);
        }
      },
      (error) => {
        setGeoError(`Geolocation error: ${error.message}`);
        setIsFetching(false);
      }
    );
  }, [onLocationDataChange]);

  /* ==============================
     ✏ Handle Manual Input
  ============================== */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onLocationDataChange(name, value);
  };

  /* ==============================
     🔎 Google Maps Link Validation
  ============================== */
  const handleGoogleMapChange = (e) => {
    const value = e.target.value.trim();
    onLocationDataChange("googleMapLink", value);

    if (!value) {
      setFormErrors((prev) => ({
        ...prev,
        googleMapLink: "Google Maps link is required.",
      }));
    } else if (
      !/^https?:\/\/(www\.)?(google\.com\/maps|maps\.app\.goo\.gl)/.test(value)
    ) {
      setFormErrors((prev) => ({
        ...prev,
        googleMapLink: "Enter a valid Google Maps URL.",
      }));
    } else {
      setFormErrors((prev) => ({
        ...prev,
        googleMapLink: "",
      }));
    }
  };

  return (
    <div className="mb-6 p-6 bg-white rounded-lg shadow-lg">
      {/* Detect Location Button */}
      <button
        type="button"
        onClick={getGeoLocation}
        className="mb-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
      >
        Detect My Location
      </button>

      {isFetching && (
        <div className="text-blue-500 mb-4">
          Fetching your location...
        </div>
      )}

      {geoError && (
        <p className="text-red-500 mb-4">{geoError}</p>
      )}

      <form>
        {/* Business Name */}
        <div className="mb-4">
          <label className="block text-gray-700 flex items-center mb-2">
            <FaMapPin className="mr-2" /> Name of Business
          </label>
          <input
            type="text"
            name="village"
            value={formData.village || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg"
          />
        </div>

        {/* City */}
        <div className="mb-4">
          <label className="block text-gray-700 flex items-center mb-2">
            <FaCity className="mr-2" /> City
          </label>
          <input
            type="text"
            name="city"
            value={formData.city || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg"
          />
        </div>

        {/* District */}
        <div className="mb-4">
          <label className="block text-gray-700 flex items-center mb-2">
            <FaSearchLocation className="mr-2" /> District
          </label>
          <input
            type="text"
            name="district"
            value={formData.district || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg"
          />
        </div>

        {/* State */}
        <div className="mb-4">
          <label className="block text-gray-700 flex items-center mb-2">
            <FaHome className="mr-2" /> State
          </label>
          <input
            type="text"
            name="state"
            value={formData.state || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg"
          />
        </div>

        {/* Country */}
        <div className="mb-4">
          <label className="block text-gray-700 flex items-center mb-2">
            <FaGlobe className="mr-2" /> Country
          </label>
          <select
            name="country"
            value={formData.country || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg"
          >
            <option value="">Select a country</option>
            {countries?.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>

        {/* Postal Code */}
        <div className="mb-4">
          <label className="block text-gray-700 flex items-center mb-2">
            <FaMailBulk className="mr-2" /> Postal Code
          </label>
          <input
            type="text"
            name="postalCode"
            value={formData.postalCode || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg"
          />
        </div>

        {/* Latitude & Longitude */}
        <div className="mb-4">
          <label className="block text-gray-700 flex items-center mb-2">
            <FaMapPin className="mr-2" /> Latitude & Longitude
          </label>
          <p className="text-sm text-gray-600">
            Latitude: {latitude || "Not available"} | Longitude:{" "}
            {longitude || "Not available"}
          </p>
        </div>

        {/* Google Maps Link */}
        <div className="mb-4">
          <label className="block text-gray-700 flex items-center mb-2">
            <FaSearchLocation className="mr-2" /> Add Exact Business Location
          </label>
          <input
            type="text"
            name="googleMapLink"
            placeholder="Enter Google Maps location URL"
            value={formData.googleMapLink || ""}
            onChange={handleGoogleMapChange}
            className={`w-full p-3 border rounded-lg ${
              formErrors.googleMapLink
                ? "border-red-500"
                : formData.googleMapLink
                ? "border-green-500"
                : "border-gray-300"
            }`}
          />
          {formErrors.googleMapLink && (
            <p className="text-red-500 text-sm mt-1">
              {formErrors.googleMapLink}
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default LocationInformation;
